import { CopyOutlined, UndoOutlined } from '@ant-design/icons';
import {
  getGetFrontRichiesteQueryKey,
  useDeleteFrontRichiesteId,
  useGetFrontRichieste,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { type CampoSemantico } from '@framework/api-client/api/inquiries-compile/schemas/campoSemantico.ts';
import { EnumRichiestaDTOStato } from '@framework/api-client/api/inquiries-compile/schemas/enumRichiestaDTOStato.ts';
import { type GetFrontRichiesteParams } from '@framework/api-client/api/inquiries-compile/schemas/getFrontRichiesteParams.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { useGetFrontTipiDomanda } from '@framework/api-client/api/inquiries-compile/tipi-domanda/tipi-domanda.ts';
import { GetTagSemanticiTipoSezione } from '@framework/api-client/api/users/schemas/getTagSemanticiTipoSezione.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { getSemanticTagColumn } from '@framework/common-web/schema/semantic-tags/getSemanticTagColumn.ts';
import { useSemanticTagColumns } from '@framework/common-web/schema/semantic-tags/hooks/useSemanticTagColumns.ts';
import { useSemanticTagSerchableExtension } from '@framework/common-web/schema/semantic-tags/hooks/useSemanticTagSerchableExtension.ts';
import { TitleSubmissionRow } from '@framework/common-web/ui/inquiry/submission/TitleSubmissionRow.tsx';
import { SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';
import { ButtonDeleteConfirm } from '@framework/ui/components/button/ButtonDeleteConfirm.tsx';
import { ResponsiveCard } from '@framework/ui/components/card/ResponsiveCard.tsx';
import { FilterBar } from '@framework/ui/components/search/filter-bar/FilterBar.tsx';
import { SwitchContenSizeButton } from '@framework/ui/components/table/column-picker/content-size/SwitchContenSizeButton.tsx';
import { EnumContentSize } from '@framework/ui/components/table/column-picker/content-size/useLocalTableContentSize.ts';
import { useTableContentSize } from '@framework/ui/components/table/column-picker/content-size/useTableContentSize.ts';
import { usePickedColumns } from '@framework/ui/components/table/column-picker/usePickedColumns.tsx';
import { PickableColumnUtils } from '@framework/ui/components/table/column-picker/utils/PickableColumnUtils.ts';
import { ColumnSortDirections } from '@framework/ui/components/table/TableColumnSort.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { getPagination } from '@framework/ui/core/pagination/getPagination.ts';
import { handlePaginationChange } from '@framework/ui/core/pagination/handlePaginationChange.ts';
import { getPaginationUrlColumns } from '@framework/ui/core/pagination/paginationUrlColumns.ts';
import { usePaginationUrlQueryState } from '@framework/ui/core/pagination/paginationUrlQuery.ts';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import {
  type BrandedPath,
  useHistory,
} from '@framework/ui/core/router/TypedRoute.tsx';
import { Divider, message, Space, Tooltip } from 'antd';
import { type ColumnType } from 'antd/es/table';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouteComponentProps } from 'react-router';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { useTheme } from 'styled-components';
import { queryClient } from '../../core/network/queryClient.ts';
import { returnFocusToPageTitle } from '../../ui/layout/header/returnFocusToPageTitle.tsx';
import { PageLayout } from '../../ui/layout/PageLayout.tsx';
import { NetworkAlertPage } from '../errors/network-alert/NetworkAlertPage.tsx';
import { LoadingPage } from '../loading/LoadingPage.tsx';
import { ExportSubmissionDropdown } from './components/file-export/ExportSubmissionDropdown.tsx';
import { SubmissionCloneButton } from './components/SubmissionDuplicateButton.tsx';
import { SubmissionEditTableButton } from './components/SubmissionEditTableButton.tsx';
import { SubmissionUndoButton } from './components/SubmissionUndoButton.tsx';
import { SubmissionViewTableButton } from './components/SubmissionViewTableButton.tsx';
import { useSubmissionSearchableFields } from './filter/useSubmissionSearchableFields.tsx';
import { SubmissionListLogic } from './SubmissionListLogic.ts';
import { GetFrontExtendedFiltersSchema } from './schema/GetFrontExtendedFiltersSchema.ts';
import {
  submissionsColumns,
  submissionsColumnsMobile,
} from './submissionsColumns.tsx';

const canDelete = ({ stato }: RichiestaDTO) => {
  return (
    stato === EnumRichiestaDTOStato.CREATA ||
    stato === EnumRichiestaDTOStato.BOZZA
  );
};

const PAGE_TITLE_ID = 'submission-list-title-focus-id';

export interface ISubmissionListPageProps extends RouteComponentProps {}

export function SubmissionListPage(props: ISubmissionListPageProps) {
  const history = useHistory();
  const user = useCurrentUser();
  const theme = useTheme();

  const isMobile = useBreakpoint(down('md')) ?? false;
  const { t } = useTranslation();

  const { contentSize, setContentSize } = useTableContentSize(
    `submission-content-size-user-preference-${
      user?.utenteLoggato?.idUtente ?? 'guest'
    }`,
  );

  const defaultQuery = useMemo(
    () =>
      getPagination({
        page: 0,
        pageSize: 10,
        sort: 'dataModifica',
        direction: ColumnSortDirections.descend,
      }),
    [],
  );
  const { data: rawInquiries } = useGetFrontTipiDomanda();
  const inquiries = useMemo(
    () => rawInquiries?.dati ?? [],
    [rawInquiries?.dati],
  );

  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: t('submission-list.richieste', 'Richieste'),
  });

  const [query, setQuery] = usePaginationUrlQueryState<GetFrontRichiesteParams>(
    {
      history,
      input: defaultQuery,
      // Non richiesto. Se presente, salva la query in locale
      // persistKey: 'frontoffice-submission-list',
    },
  );

  const { data, error, isLoading, isFetching } = useGetFrontRichieste(query, {
    query: { keepPreviousData: true },
    request: { arrayFormat: 'repeat' },
  });

  const submissions = useMemo(
    () =>
      // Con l'aggiornamento API sulla gestione dei soggetti (viewer/editor)
      // il BE restituisce in datiTitolare anche _tutti i dati dei soggetti_
      // (viewer/editor). Per evitare che questa breaking change impatti
      // sull'applicazione, filtro i datiTitolare per restituire solo i soggetti
      // di tipo Titolare.
      SubmissionLogic.filterSubmissionsApplicantData(data?.dati),
    [data?.dati],
  );
  const meta = data?.meta;

  const defaultColumns = isMobile
    ? submissionsColumnsMobile
    : submissionsColumns;

  // Crea un array di "campiSemantici" rimuovendo tutti gli elementi non definiti
  const semanticFieldList = useMemo(
    () =>
      submissions
        ?.flatMap(s => s.campiSemantici)
        .filter((c): c is CampoSemantico => Boolean(c)) ?? [],

    [submissions],
  );

  const semanticTagSearchableOptions = useSemanticTagSerchableExtension({
    sectionType: GetTagSemanticiTipoSezione.FRONTOFFICE,
  });

  // Colonne automatiche generate per la visualizzazione dei campi semantici.
  const semanticTagColumns = useSemanticTagColumns({
    enabled:
      Boolean(query.idGruppoTipoDomanda) && !PickableColumnUtils.isEnabled(),
    semanticFields: semanticFieldList,
    semanticTags: semanticTagSearchableOptions.semanticTags,
  });

  const searchableFields = useSubmissionSearchableFields({
    extraFields: semanticTagSearchableOptions.searchableFields,
  });

  const deleteMutation = useDeleteFrontRichiesteId({
    mutation: {
      onSuccess: () => {
        message.success(
          t(
            'submission-list.richiesta-eliminata-con-successo',
            'Richiesta eliminata con successo',
          ),
        );
        queryClient.invalidateQueries(getGetFrontRichiesteQueryKey());
      },
      onError: error => {
        message.error(
          getNetworkErrorMessage(
            error,
            t(
              'submission-list.errore-eliminazione-richiesta',
              "Errore durante l'eliminazione della richiesta",
            ),
          ),
        );
      },
    },
  });

  const handleDelete = useCallback(
    (submission: RichiestaDTO) => {
      deleteMutation.mutate({ id: submission.id! });
    },
    [deleteMutation],
  );

  const inputColumns = useMemo(
    () => [
      ...defaultColumns,
      ...semanticTagColumns,
      {
        key: 'action',
        align: 'right',
        width: 95,
        render: (text, record) => (
          <Space>
            {canDelete(record) && (
              <ButtonDeleteConfirm
                iconOnlyButton
                buttonType="text"
                buttonDanger
                onConfirm={() => {
                  handleDelete(record);
                }}
              />
            )}
            {SubmissionListLogic.showSubmissionListEditButton() &&
            !SubmissionLogic.isSubmissionSent(record) ? (
              <SubmissionEditTableButton record={record} isMobile={isMobile} />
            ) : (
              <SubmissionViewTableButton
                submissionId={record.id}
                isMobile={isMobile}
              />
            )}

            {SubmissionLogic.canUndo(record) && (
              <SubmissionUndoButton
                iconOnlyButton
                size="small"
                buttonType="link"
                submissionId={record.id}
                tooltip="Annulla richiesta"
                buttonIcon={
                  <UndoOutlined
                    aria-label={t(
                      'submission-list.annulla-richiesta',
                      'Annulla la Richiesta',
                    )}
                  />
                }
              />
            )}
            {SubmissionLogic.canDuplicate(record, inquiries) && (
              <Tooltip
                title={t(
                  'submission-list.duplica-richiesta',
                  'Duplica richiesta',
                )}
              >
                <SubmissionCloneButton
                  iconOnlyButton
                  type="link"
                  size="small"
                  submissionId={record.id}
                  isJustCreated={record.stato === EnumRichiestaDTOStato.CREATA}
                  icon={
                    <CopyOutlined
                      aria-label={t(
                        'submission-list.duplica-richiesta',
                        'Duplica richiesta',
                      )}
                    />
                  }
                />
              </Tooltip>
            )}
          </Space>
        ),
      } as ColumnType<RichiestaDTO>,
    ],
    [defaultColumns, handleDelete, inquiries, isMobile, t, semanticTagColumns],
  );

  const extraColumns = useMemo(
    () =>
      semanticTagSearchableOptions.semanticTags.map(t =>
        getSemanticTagColumn(t, GetTagSemanticiTipoSezione.FRONTOFFICE),
      ),
    [semanticTagSearchableOptions.semanticTags],
  );

  const { columns: customColumns, fixedColumnProps } = usePickedColumns(
    inputColumns,
    {
      id: `submission-list-${user?.utenteLoggato?.idUtente ?? 'guest'}`,
      extra: (
        <SwitchContenSizeButton
          onChangeSize={size => setContentSize(size)}
          currentContentSize={contentSize}
        />
      ),
      extraColumns,
    },
  );

  const columns = getPaginationUrlColumns<
    RichiestaDTO,
    GetFrontRichiesteParams
  >(customColumns, query, {
    progressivo: ['progressivoId', 'progressivoAnno'],
  });

  const onPaginationChange = handlePaginationChange<
    RichiestaDTO,
    GetFrontRichiesteParams
  >({
    columns,
    defaultQuery,
    query,
    setQuery,
    // Non ci sono filtri in tabella, sono gestiti tutti dall'avdvanced search
    getFilters: filters => ({}),
  });

  if (isLoading ?? isFetching) return <LoadingPage />;

  if (error) {
    return (
      <NetworkAlertPage
        message={t(
          'submission-list.impossibile-caricare-elenco-richieste',
          "Impossibile caricare l'elenco delle Richieste",
        )}
        error={error}
      />
    );
  }

  const filterBar = (
    <FilterBar
      {...fixedColumnProps}
      isFetching={isFetching}
      total={meta?.totale ?? undefined}
      id="submission-list"
      schema={GetFrontExtendedFiltersSchema}
      fields={searchableFields}
      query={query}
      setQuery={setQuery}
      defaultQuery={defaultQuery}
      pageKey="pagina"
      transform={semanticTagSearchableOptions.transform}
      extraActions={<ExportSubmissionDropdown query={query} />}
      layout={{ showInCard: true }}
      messageListProps={{
        message: t(
          'submission-list.domande-trovate-in-base-ai-filtri',
          'domande trovate in base ai filtri di ricerca.',
        ),
        resetButtonProps: {
          onClick: () => {
            setQuery(defaultQuery);
            returnFocusToPageTitle(PAGE_TITLE_ID);
          },
          children: t('submission-list.rimuovi-filtri', 'Rimuovi filtri'),
        },
      }}
    />
  );

  return (
    <PageLayout
      isTablePage
      boxedContent={contentSize === EnumContentSize.BOXED}
    >
      <ResponsiveCard
        hideCardHeader={theme.searchButtonOnTableFlag}
        noPadding
        title={
          <TitleSubmissionRow id={PAGE_TITLE_ID}>
            {t('submission-list.submissions-list-title', 'Elenco Richieste')}
          </TitleSubmissionRow>
        }
      >
        {!theme.searchButtonOnTableFlag && <Divider style={{ margin: 0 }} />}

        <TableData
          title={r => <div>{filterBar}</div>}
          darkTitleBar={false}
          {...fixedColumnProps}
          showSorterTooltip={false}
          size="small"
          dataSource={submissions ?? []}
          loading={isLoading || isFetching}
          pagination={{
            current: Number(query.pagina ?? 0) + 1,
            pageSize: query.limite != null ? Number(query.limite) : 10,
            total: meta?.totale ?? 0,
            pageSizeOptions: [5, 10, 20, 50],
          }}
          onChange={onPaginationChange}
          columns={columns}
        />
      </ResponsiveCard>
    </PageLayout>
  );
}
